import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatProjectNumber } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { PageRoot, PageHeader, Toolbar } from '../ui/Page';
import { SearchInput } from '../ui/SearchInput';
import { DataTable, dash, type DataTableColumn } from '@/components/ui/data-table';
import { useServerTable } from '../ui/useServerTable';
import { CustomerSearchSelect } from '../ui/CustomerSearchSelect';
import { StatusBadge, Notice, type Tone } from '../ui/Badge';
import { Button } from '../ui/Button';
import { can } from '@servwell/shared';
import { useSession } from '../session';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState } from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { Sheet } from '../ui/Sheet';
import { Field, field } from '../create/Field';
import { IconProjects, IconPlus } from '../icons';

type Row = {
  project: {
    id: string;
    projectNumber: number;
    name: string;
    status: string;
    startsOn: string | null;
    targetEndOn: string | null;
  };
  customerName: string;
  jobCount: number;
};

export const PROJECT_STATUS: Record<string, { label: string; tone: Tone }> = {
  planned: { label: 'Planned', tone: 'neutral' },
  active: { label: 'Active', tone: 'info' },
  on_hold: { label: 'On hold', tone: 'warn' },
  closed: { label: 'Closed', tone: 'success' },
  canceled: { label: 'Canceled', tone: 'neutral' },
};

/**
 * Projects (PRD v2 §6.8) — the aggregation spine for commercial work. A residential shop
 * will open this page once, see nothing, and never come back, which is the correct outcome:
 * every association to a project is optional, so the feature costs them nothing.
 */
export function ProjectsPage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { search, setSearch, query, table } = useServerTable();
  const q = useQuery({
    ...trpc.project.list.queryOptions(query),
    placeholderData: (prev) => prev,
  });
  const [creating, setCreating] = useState(false);
  const canManage = can(useSession().role, 'project', 'manage');

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'name',
      label: 'Project',
      value: (r) => r.project.name,
      searchValue: (r) =>
        `${r.project.name} ${formatProjectNumber(r.project.projectNumber)}`,
      cell: (r) => (
        <div>
          <p className="font-medium">{r.project.name}</p>
          <p className="text-xs text-muted-foreground">
            {formatProjectNumber(r.project.projectNumber)}
          </p>
        </div>
      ),
    },
    {
      key: 'customer',
      label: 'Customer',
      value: (r) => r.customerName,
      cell: (r) => r.customerName,
    },
    {
      key: 'status',
      label: 'Status',
      value: (r) => r.project.status,
      cell: (r) => {
        const s = PROJECT_STATUS[r.project.status] ?? {
          label: r.project.status,
          tone: 'neutral' as Tone,
        };
        return <StatusBadge tone={s.tone}>{s.label}</StatusBadge>;
      },
    },
    {
      key: 'jobs',
      label: 'Jobs',
      align: 'right',
      value: (r) => r.jobCount,
      searchValue: () => null,
      cell: (r) =>
        r.jobCount === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="tabular-nums">{r.jobCount}</span>
        ),
    },
    {
      key: 'starts',
      label: 'Starts',
      value: (r) => r.project.startsOn,
      cell: (r) => dash(r.project.startsOn),
    },
    {
      key: 'target',
      label: 'Target end',
      value: (r) => r.project.targetEndOn,
      cell: (r) => dash(r.project.targetEndOn),
    },
  ];

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    return (
      <PageRoot>
        <PageHeader title="Projects" />
        <ErrorState
          icon={IconProjects}
          what="your projects"
          onRetry={() => void q.refetch()}
        />
      </PageRoot>
    );
  }

  return (
    <PageRoot>
      <PageHeader
        title="Projects"
        sub="One customer, many work orders, one answer on whether it made money."
        right={
          /* A bookkeeper reads this page — `project:read` is theirs, granted in the matrix
             with the note that a project IS a P&L view. They do not open projects, so the
             button is not drawn for them: an affordance nobody may use is a 403 waiting for
             a click. `project:manage` is the write half. */
          canManage ? (
            <Button onClick={() => setCreating(true)}>
              <IconPlus size={14} />
              New project
            </Button>
          ) : null
        }
      />

      <Toolbar>
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search projects"
          aria-label="Search projects"
        />
      </Toolbar>

      <DataTable
        tableId="projects"
        columns={columns}
        rows={(q.data?.rows ?? []) as Row[]}
        rowKey={(r) => r.project.id}
        search={search}
        server={table(q.data?.total ?? 0, q.isFetching)}
        onRowClick={(r) =>
          void navigate({
            to: '/projects/$projectId',
            params: { projectId: r.project.id },
          })
        }
        rowLabel={(r) => `Open ${r.project.name}`}
        empty={
          <EmptyState
            icon={IconProjects}
            title="No projects yet"
            description="Open one for a build with several work orders. Quotes, jobs, invoices and purchase orders can all hang off it — and then the margin is one number instead of nine."
            action={
              canManage ? (
                <Button onClick={() => setCreating(true)}>Start a project</Button>
              ) : undefined
            }
          />
        }
      />

      {creating ? (
        <NewProjectSheet
          onClose={() => setCreating(false)}
          onCreated={(id) =>
            void navigate({ to: '/projects/$projectId', params: { projectId: id } })
          }
        />
      ) : null}
    </PageRoot>
  );
}

function NewProjectSheet({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [targetEndOn, setTargetEndOn] = useState('');

  // Only fetched once a customer is picked — a project's property list is that customer's.
  const properties = useQuery({
    ...trpc.customer.properties.queryOptions({ customerId }),
    enabled: Boolean(customerId),
  });

  const create = useMutation(
    trpc.project.create.mutationOptions({
      onSuccess: (row) => {
        void qc.invalidateQueries();
        onCreated(row.id);
      },
    }),
  );

  const canSave = name.trim().length > 0 && customerId && !create.isPending;

  return (
    <Sheet
      open
      onClose={onClose}
      title="New project"
      footer={
        <Button
          className="w-full"
          disabled={!canSave}
          onClick={() =>
            create.mutate({
              name: name.trim(),
              customerId,
              propertyId: propertyId || null,
              startsOn: startsOn || undefined,
              targetEndOn: targetEndOn || undefined,
              ...idem(),
            })
          }
        >
          {create.isPending ? 'Creating…' : 'Create project'}
        </Button>
      }
    >
      <div className="space-y-3">
        {create.error ? <Notice tone="danger">{create.error.message}</Notice> : null}
        <Field label="Name">
          <input
            className={field}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Northgate rooftop replacement"
            autoFocus
          />
        </Field>
        {/* Typed search, not a dropdown of every customer — see CustomerSearchSelect. */}
        <CustomerSearchSelect
          value={customerId}
          onChange={(id) => {
            setCustomerId(id);
            setPropertyId('');
          }}
        />
        <Field label="Property (optional)">
          <select
            className={field}
            value={propertyId}
            disabled={!customerId}
            onChange={(e) => setPropertyId(e.target.value)}
          >
            {/* A project that spans buildings is half the reason projects exist. */}
            <option value="">Across their properties</option>
            {(properties.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Starts">
            <input
              className={field}
              type="date"
              value={startsOn}
              onChange={(e) => setStartsOn(e.target.value)}
            />
          </Field>
          <Field label="Target end">
            <input
              className={field}
              type="date"
              value={targetEndOn}
              onChange={(e) => setTargetEndOn(e.target.value)}
            />
          </Field>
        </div>
      </div>
    </Sheet>
  );
}
